import { isBase64Image, isInlineDataImage } from 'utils/Image'

export class Label {
  static getDefaultValue (featureProperties: { [key: string]: any }): string {
    const defaultProperty = this.getDefaultProperty(featureProperties)
    // console.debug('Label - getDefaultValue', defaultProperty)

    if (defaultProperty) {
      // If the found property was a fallback, include the property name in the return value
      if (defaultProperty.fallback) {
        const fallbackValue = `${defaultProperty.name}: ${defaultProperty.value}`
        return fallbackValue
      }

      return defaultProperty.value
    }

    // No property was found: last resort
    return 'Untitled'
  }

  static getDefaultProperty (featureProperties: { [key: string]: any }): any {
    // console.debug('Label - getDefaultProperty', featureProperties)

    const isValidKey = (key: string) => {
      // Internal fields (i.e. starting with '$') are not valid
      if (/^\$\w+$/.test(key)) {
        return false
      }

      return true
    }

    // Convenience fuction to check if a value is good enough to be used
    const isValidValue = (value: any) => {
      const isValid =
        typeof value !== 'undefined' &&
        value !== null &&
        value !== '' &&
        !isBase64Image(value) &&
        !isInlineDataImage(value)

      return isValid
    }

    const stringProperties = this.getStringProperties(featureProperties)
    const firstStringPropertyWithValue = stringProperties.find(({ name, value }) => isValidKey(name) && isValidValue(value))

    // console.debug('Label - getDefaultProperty - stringProperties', firstStringPropertyWithValue, stringProperties)

    // If we found a property with a string value, return this property
    if (firstStringPropertyWithValue) {
      return firstStringPropertyWithValue
    }

    // Fallback option:
    // No string value has been found, we'll pick the first valid attribute with any valid value
    const featurePropertiesArray = Object.entries(featureProperties)
    const firstProp = featurePropertiesArray.find(([name, value]) => isValidKey(name) && isValidValue(value))

    // console.debug('Label - getDefaultProperty - fallback firstProp', firstProp, featurePropertiesArray)

    if (firstProp) {
      const [name, value] = firstProp

      // Create an object with the key/value and make clear this is a fallback property
      const fallbackProperty = {
        name,
        value,
        fallback: true
      }

      return fallbackProperty
    }

    return null
  }

  private static getStringProperties (featureProperties: { [key: string]: any }): Record<string, any>[] {
    // console.debug('Label - getStringProperties', featureProperties)

    const featurePropertiesKeys = Object.keys(featureProperties)
    const stringProperties = featurePropertiesKeys
      .filter(key => {
        const value = featureProperties[key]

        // Ignore any value that is not a string or an empty string
        if (typeof value !== 'string' || value.trim() === '') {
          return false
        }

        return true
      })
      .map(key => this.addRank(key, featureProperties[key]))
      .sort((a, b) => a.rank - b.rank)

    // console.debug('Label - getStringProperties - stringProperties', stringProperties)

    return stringProperties
  }

  private static addRank (key: string, value: string): { name: string, value: string, rank: number} {
    if (key.toUpperCase() === 'LABEL') {
      return { name: key, value, rank: 1000 }
    } else if (key.toUpperCase().endsWith('LABEL')) {
      return { name: key, value, rank: 2000 }
    } else if (key.toUpperCase() === 'EZI_ADDRESS') {
      return { name: key, value, rank: 2500 }
    } else if (key.toUpperCase() === 'NAME') {
      return { name: key, value, rank: 3000 }
    } else if (key.toUpperCase().endsWith('NAME')) {
      return { name: key, value, rank: 4000 }
    } else if (key.toUpperCase() === 'TITLE') {
      return { name: key, value, rank: 5000 }
    } else if (key.toUpperCase().endsWith('TITLE')) {
      return { name: key, value, rank: 6000 }
    } else if (key.toUpperCase() === 'DESCRIPTION') {
      return { name: key, value, rank: 7000 }
    } else if (key.toUpperCase().endsWith('DESCRIPTION')) {
      return { name: key, value, rank: 8000 }
    } else {
      return { name: key, value, rank: Number.MAX_SAFE_INTEGER }
    }
  }
}
